import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import type { Customer } from '@metron/shared';
import { Input } from './ui/input.tsx';
import { cn } from '../lib/utils';

export type CustomerSearchField =
  | 'name'
  | 'id'
  | 'accountName'
  | 'salesforceId'
  | 'metronId'
  | 'accountType'
  | 'realEstateAccountType';

const ALL_SEARCH_FIELDS: ReadonlyArray<CustomerSearchField> = [
  'name',
  'id',
  'accountName',
  'salesforceId',
  'metronId',
  'accountType',
  'realEstateAccountType',
];

function matchesCustomer(
  customer: Customer,
  query: string,
  fields: ReadonlyArray<CustomerSearchField>,
): boolean {
  const q = query.toLowerCase();
  for (const field of fields) {
    switch (field) {
      case 'name':
        if (customer.waterscopeAccountName.toLowerCase().includes(q)) return true;
        break;
      case 'id':
        if (customer.id.toString().includes(q)) return true;
        break;
      case 'accountName':
        if (customer.waterscopeAccountId?.toLowerCase().includes(q)) return true;
        break;
      case 'salesforceId':
        if (customer.salesforceId?.toLowerCase().includes(q)) return true;
        break;
      case 'metronId':
        if (customer.metronId?.toLowerCase().includes(q)) return true;
        break;
      case 'accountType':
        if (customer.accountType.toLowerCase().includes(q)) return true;
        break;
      case 'realEstateAccountType':
        if (customer.realEstateAccountType?.toLowerCase().includes(q)) return true;
        break;
    }
  }
  return false;
}

export type CustomerPickerProps = {
  customers: Customer[];
  selectedCustomerId: number | null | undefined;
  onSelect: (customerId: number) => void;
  /** When provided, a Clear button is shown next to the selected customer. */
  onClear?: () => void;
  isLoading?: boolean;
  /**
   * Locked: the selection cannot be changed. The picker renders exactly the
   * same controls as when enabled — the search input and the "Selected:" line
   * — with the whole control faded and inert (MET-3237 row 13).
   */
  disabled?: boolean;
  searchFields?: ReadonlyArray<CustomerSearchField>;
  placeholder?: string;
  maxResults?: number;
  className?: string;
  inputId?: string;
};

export function CustomerPicker(props: CustomerPickerProps) {
  const {
    customers,
    selectedCustomerId,
    onSelect,
    onClear,
    isLoading,
    disabled = false,
    searchFields = ALL_SEARCH_FIELDS,
    placeholder = 'Search customers…',
    maxResults = 50,
    className,
    inputId,
  } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const trimmed = searchQuery.trim();
  const showPicker = !disabled && trimmed.length > 0;

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId) ?? null,
    [customers, selectedCustomerId],
  );

  const filtered = useMemo(() => {
    if (!showPicker) return [];
    return customers.filter((c) => matchesCustomer(c, trimmed, searchFields));
  }, [customers, trimmed, showPicker, searchFields]);

  const visible = filtered.slice(0, maxResults);
  const overflow = filtered.length > maxResults;

  // When locked, the container carries the single 50% fade (matching the User
  // Type toggle) and the input's own `disabled:opacity-50` is suppressed so the
  // two do not multiply into a washed-out input.
  return (
    <div
      className={cn(disabled && 'opacity-50 cursor-not-allowed', className)}
      aria-disabled={disabled ? 'true' : undefined}
    >
      <div className='relative'>
        <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
        <Input
          id={inputId}
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn('pl-8', disabled && 'disabled:opacity-100')}
          disabled={isLoading || disabled}
        />
      </div>

      {showPicker && (
        <div className='mt-2 border rounded-md max-h-80 overflow-y-auto'>
          {filtered.length === 0
            ? <p className='p-3 text-sm text-muted-foreground'>No customers match.</p>
            : (
              visible.map((customer) => (
                <button
                  key={customer.id}
                  type='button'
                  onClick={() => {
                    onSelect(customer.id);
                    setSearchQuery('');
                  }}
                  className='w-full text-left px-3 py-2 hover:bg-accent transition-colors border-b last:border-b-0'
                >
                  <div className='font-medium'>{customer.waterscopeAccountName}</div>
                  <div className='text-xs text-muted-foreground'>
                    ID {customer.id} · {customer.accountType}
                  </div>
                </button>
              ))
            )}
          {overflow && (
            <p className='p-2 text-xs text-muted-foreground text-center bg-muted'>
              Showing first {maxResults} of {filtered.length} matches — refine your search.
            </p>
          )}
        </div>
      )}

      {!showPicker && selectedCustomer && (
        <p className='mt-2 text-sm text-muted-foreground'>
          Selected:{' '}
          <span className='font-medium text-foreground'>
            {selectedCustomer.waterscopeAccountName}
          </span>{' '}
          (ID {selectedCustomer.id})
          {onClear && (
            <button
              type='button'
              onClick={onClear}
              disabled={isLoading || disabled}
              className='ml-2 inline-flex items-center gap-0.5 text-xs underline underline-offset-2 hover:text-foreground'
            >
              <X className='h-3 w-3' aria-hidden='true' />
              Clear
            </button>
          )}
        </p>
      )}
    </div>
  );
}
